import React, { useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Paper,
  Chip,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Divider,
  Button,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { HealthScoreConfiguration, HealthScoreComponent } from '../../types';
import { PrimaryButton } from '../common/PrimaryButton';
import { SecondaryButton } from '../common/SecondaryButton';

interface HealthScoreConfigProps {
  healthScoreConfig: HealthScoreConfiguration | undefined;
  onChange: (healthScoreConfig: HealthScoreConfiguration) => void;
  entityDefinitions: Record<string, any>;
  featureDefinitions: Record<string, any>;
}

const TYPE_COLORS: Record<string, string> = {
  entity_frequency: '#4caf50',
  feature: '#2196f3',
  metadata: '#ff9800',
};

const TYPE_LABELS: Record<string, string> = {
  entity_frequency: 'Entity Frequency',
  feature: 'Feature',
  metadata: 'Metadata',
};

const RECENCY_DECAY = [
  { label: 'Today', days: 0, points: 50 },
  { label: '1 day', days: 1, points: 45 },
  { label: '3 days', days: 3, points: 40 },
  { label: '7 days', days: 7, points: 30 },
  { label: '14 days', days: 14, points: 20 },
  { label: '30 days', days: 30, points: 10 },
  { label: '30+ days', days: 31, points: 0 },
];

export const HealthScoreConfig: React.FC<HealthScoreConfigProps> = ({
  healthScoreConfig,
  onChange,
  entityDefinitions,
  featureDefinitions,
}) => {
  const components = healthScoreConfig?.components || [];
  const [addingComponent, setAddingComponent] = useState(false);
  const [newType, setNewType] = useState<'entity_frequency' | 'feature' | 'metadata' | ''>('');
  const [newEntity, setNewEntity] = useState('');
  const [newFeature, setNewFeature] = useState('');
  const [newMetadataField, setNewMetadataField] = useState('');
  const [newMetadataValue, setNewMetadataValue] = useState('');
  const [newMaxPoints, setNewMaxPoints] = useState(10);

  const entityNames = Object.keys(entityDefinitions);
  const featureNames = Object.keys(featureDefinitions);

  const configurableTotal = components.reduce((sum, c) => sum + (c.maxPoints || 0), 0);
  const totalAllocated = 50 + configurableTotal;

  const updateComponents = (updated: HealthScoreComponent[]) => {
    onChange({ components: updated });
  };

  const handleUpdateComponent = (index: number, updates: Partial<HealthScoreComponent>) => {
    const updated = components.map((c, i) => (i === index ? { ...c, ...updates } : c));
    updateComponents(updated);
  };

  const handleDeleteComponent = (index: number) => {
    updateComponents(components.filter((_, i) => i !== index));
  };

  const handleAddThreshold = (index: number) => {
    const comp = components[index];
    const thresholds = [...(comp.thresholds || []), { minPerWeek: 1, percentage: 33 }];
    handleUpdateComponent(index, { thresholds });
  };

  const handleUpdateThreshold = (compIndex: number, threshIndex: number, updates: Partial<{ minPerWeek: number; percentage: number }>) => {
    const comp = components[compIndex];
    const thresholds = (comp.thresholds || []).map((t, i) => (i === threshIndex ? { ...t, ...updates } : t));
    handleUpdateComponent(compIndex, { thresholds });
  };

  const handleDeleteThreshold = (compIndex: number, threshIndex: number) => {
    const comp = components[compIndex];
    const thresholds = (comp.thresholds || []).filter((_, i) => i !== threshIndex);
    handleUpdateComponent(compIndex, { thresholds });
  };

  /** 'true'/'false' → boolean, numeric strings → number, '' → undefined. */
  const coerceRequiredValue = (raw: string): any => {
    const v = raw.trim();
    if (v === '') return undefined;
    if (v === 'true') return true;
    if (v === 'false') return false;
    if (/^-?\d+(\.\d+)?$/.test(v)) return Number(v);
    return v;
  };

  const handleSaveNewComponent = () => {
    if (!newType) return;

    let comp: HealthScoreComponent;

    if (newType === 'entity_frequency') {
      if (!newEntity) return;
      comp = {
        name: `${newEntity}_frequency`,
        type: 'entity_frequency',
        maxPoints: newMaxPoints,
        entity: newEntity,
        thresholds: [
          { minPerWeek: 5, percentage: 100 },
          { minPerWeek: 2, percentage: 67 },
          { minPerWeek: 1, percentage: 33 },
        ],
      };
    } else if (newType === 'feature') {
      if (!newFeature) return;
      comp = {
        name: `${newFeature}_adoption`,
        type: 'feature',
        maxPoints: newMaxPoints,
        feature: newFeature,
        requiredValue: true,
      };
    } else {
      if (!newMetadataField) return;
      comp = {
        name: `${newMetadataField}_check`,
        type: 'metadata',
        maxPoints: newMaxPoints,
        // The backend evaluator reads `field`; `metadata` is kept for older configs.
        field: newMetadataField,
        metadata: newMetadataField,
        requiredValue: coerceRequiredValue(newMetadataValue) ?? true,
      };
    }

    updateComponents([...components, comp]);
    resetAddForm();
  };

  const resetAddForm = () => {
    setAddingComponent(false);
    setNewType('');
    setNewEntity('');
    setNewFeature('');
    setNewMetadataField('');
    setNewMetadataValue('');
    setNewMaxPoints(10);
  };

  // Input style helpers (matching StageDefinitions patterns)
  const smallInputSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: '#ffd9a8',
      color: '#1B0826',
      fontWeight: 500,
      fontSize: '13px',
      height: '26px',
      borderRadius: '16px',
      '& fieldset': { border: 'none' },
      '& input': { py: 0.5, px: 1 },
    },
  };

  const greenInputSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: '#b8e6d5',
      color: '#1B0826',
      fontWeight: 500,
      fontSize: '13px',
      height: '26px',
      borderRadius: '16px',
      '& fieldset': { border: 'none' },
      '& input': { py: 0.5, px: 1 },
    },
  };

  const selectSx = (color: string) => ({
    bgcolor: color,
    color: '#1B0826',
    fontWeight: 500,
    fontSize: '13px',
    height: '26px',
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& .MuiSelect-select': { py: 0.5, px: 1 },
    borderRadius: '16px',
  });

  return (
    <Box>
      {/* Header */}
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Health Score Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Configure scoring components. Recency (50 pts) is automatic. Add up to 50 configurable points.
      </Typography>

      {/* Budget Bar */}
      <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2" fontWeight={600}>Point Budget</Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            color={configurableTotal > 50 ? 'error.main' : 'text.secondary'}
          >
            {totalAllocated} / 100 allocated
          </Typography>
        </Box>
        <Box
          sx={{
            width: '100%',
            height: 32,
            borderRadius: '16px',
            bgcolor: '#f0f0f0',
            overflow: 'hidden',
            display: 'flex',
          }}
        >
          {/* Recency segment */}
          <Tooltip title="Recency: 50 pts (system)">
            <Box sx={{ width: '50%', bgcolor: '#9e9e9e', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="caption" fontWeight={600} color="white" sx={{ fontSize: '11px' }}>
                Recency (50)
              </Typography>
            </Box>
          </Tooltip>
          {/* Component segments */}
          {components.map((comp, idx) => {
            const widthPct = Math.min(comp.maxPoints, 50) / 100 * 100;
            const color = TYPE_COLORS[comp.type] || '#9e9e9e';
            return (
              <Tooltip key={idx} title={`${comp.name}: ${comp.maxPoints} pts`}>
                <Box
                  sx={{
                    width: `${widthPct}%`,
                    bgcolor: color,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: comp.maxPoints > 0 ? '2px' : 0,
                    borderLeft: idx === 0 ? 'none' : '1px solid rgba(255,255,255,0.3)',
                  }}
                >
                  {widthPct > 4 && (
                    <Typography variant="caption" fontWeight={600} color="white" sx={{ fontSize: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', px: 0.5 }}>
                      {comp.maxPoints}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
        {/* Legend */}
        <Box display="flex" gap={2} mt={1} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: '#9e9e9e' }} />
            <Typography variant="caption" color="text.secondary">Recency</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: '#4caf50' }} />
            <Typography variant="caption" color="text.secondary">Entity Frequency</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: '#2196f3' }} />
            <Typography variant="caption" color="text.secondary">Feature</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: '#ff9800' }} />
            <Typography variant="caption" color="text.secondary">Metadata</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Over budget: the backend rejects this configuration */}
      {configurableTotal > 50 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Configurable components total {configurableTotal} points but only 50 are available
          (recency uses the other 50). Reduce max points before saving; the server will reject this configuration.
        </Alert>
      )}

      {/* Recency Card (locked) */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          border: '1px solid #bdbdbd',
          borderRadius: 2,
          bgcolor: '#fafafa',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle1" fontWeight={600}>RECENCY</Typography>
            <Chip label="System" size="small" color="primary" />
          </Box>
          <Typography variant="body2" fontWeight={600} color="text.secondary">50 points</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          Based on days since last activity. Automatically calculated for all accounts.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 0.5,
            bgcolor: '#f5f5f5',
            borderRadius: 1,
            p: 1,
          }}
        >
          {RECENCY_DECAY.map((d) => (
            <Box key={d.label} textAlign="center">
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
                {d.label}
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: '13px' }}>
                {d.points}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      <Divider sx={{ my: 2 }} />

      {/* Component Cards */}
      {components.map((comp, index) => {
        const borderColor = TYPE_COLORS[comp.type] || '#e0e0e0';

        return (
          <Paper
            // Keyed by position, not by the editable name, so typing in the
            // name field does not remount the card and steal focus.
            key={index}
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              border: '1px solid #e0e0e0',
              borderLeft: `3px solid ${borderColor}`,
              borderRadius: 2,
            }}
          >
            {/* Header row */}
            <Box display="flex" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
              <TextField
                value={comp.name}
                onChange={(e) => handleUpdateComponent(index, { name: e.target.value })}
                size="small"
                sx={{
                  flex: 1,
                  minWidth: 150,
                  ...greenInputSx,
                }}
              />
              <Chip
                label={TYPE_LABELS[comp.type] || comp.type}
                size="small"
                sx={{
                  bgcolor: borderColor,
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '11px',
                }}
              />
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="caption" color="text.secondary">pts:</Typography>
                <TextField
                  type="number"
                  value={comp.maxPoints}
                  onChange={(e) => {
                    const val = Math.max(0, Math.min(50, parseInt(e.target.value) || 0));
                    handleUpdateComponent(index, { maxPoints: val });
                  }}
                  size="small"
                  sx={{ width: 60, ...smallInputSx }}
                  inputProps={{ min: 0, max: 50 }}
                />
              </Box>
              <IconButton size="small" color="error" onClick={() => handleDeleteComponent(index)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Type-specific body */}
            {comp.type === 'entity_frequency' && (
              <Box>
                <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                  <Typography variant="caption" color="text.secondary">Entity:</Typography>
                  <FormControl size="small">
                    <Select
                      value={comp.entity || ''}
                      onChange={(e) => handleUpdateComponent(index, { entity: e.target.value })}
                      displayEmpty
                      sx={selectSx('#b8e6d5')}
                    >
                      <MenuItem value=""><em>select entity</em></MenuItem>
                      {entityNames.map((name) => (
                        <MenuItem key={name} value={name}>
                          {entityDefinitions[name]?.name || entityDefinitions[name]?.displayName || name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Thresholds */}
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                  Thresholds:
                </Typography>
                {(comp.thresholds || []).map((thresh, tIdx) => (
                  <Box key={tIdx} display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
                      min/week:
                    </Typography>
                    <TextField
                      type="number"
                      value={thresh.minPerWeek ?? 0}
                      onChange={(e) => handleUpdateThreshold(index, tIdx, { minPerWeek: parseInt(e.target.value) || 0 })}
                      size="small"
                      sx={{ width: 60, ...smallInputSx }}
                      inputProps={{ min: 0 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      =
                    </Typography>
                    <TextField
                      type="number"
                      value={thresh.percentage}
                      onChange={(e) => handleUpdateThreshold(index, tIdx, { percentage: parseInt(e.target.value) || 0 })}
                      size="small"
                      sx={{ width: 60, ...smallInputSx }}
                      inputProps={{ min: 0, max: 100 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      % ({Math.round((thresh.percentage / 100) * comp.maxPoints)} pts)
                    </Typography>
                    <IconButton size="small" color="error" onClick={() => handleDeleteThreshold(index, tIdx)}>
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleAddThreshold(index)}
                  sx={{
                    mt: 0.5,
                    color: '#1B0826',
                    fontSize: '12px',
                    textTransform: 'none',
                  }}
                >
                  Add Threshold
                </Button>
              </Box>
            )}

            {comp.type === 'feature' && (
              <Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="caption" color="text.secondary">Feature:</Typography>
                  <FormControl size="small">
                    <Select
                      value={comp.feature || ''}
                      onChange={(e) => handleUpdateComponent(index, { feature: e.target.value })}
                      displayEmpty
                      sx={selectSx('#b8e6d5')}
                    >
                      <MenuItem value=""><em>select feature</em></MenuItem>
                      {featureNames.map((name) => (
                        <MenuItem key={name} value={name}>
                          {featureDefinitions[name]?.displayName || name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Chip
                    label="enabled = true"
                    size="small"
                    sx={{ bgcolor: '#ffd9a8', color: '#1B0826', fontWeight: 500, fontSize: '12px' }}
                  />
                </Box>
              </Box>
            )}

            {comp.type === 'metadata' && (
              <Box>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography variant="caption" color="text.secondary">Field:</Typography>
                  <TextField
                    value={comp.field || comp.metadata || ''}
                    onChange={(e) => handleUpdateComponent(index, { field: e.target.value, metadata: e.target.value })}
                    size="small"
                    placeholder="field name"
                    sx={{ width: 140, ...greenInputSx }}
                  />
                  <Typography variant="caption" color="text.secondary">=</Typography>
                  <TextField
                    value={comp.requiredValue !== undefined ? String(comp.requiredValue) : ''}
                    onChange={(e) => handleUpdateComponent(index, { requiredValue: coerceRequiredValue(e.target.value) ?? e.target.value })}
                    size="small"
                    placeholder="true / false / value"
                    sx={{ width: 140, ...smallInputSx }}
                  />
                </Box>
              </Box>
            )}
          </Paper>
        );
      })}

      {/* Add Component */}
      {!addingComponent ? (
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={() => setAddingComponent(true)}
          fullWidth
          sx={{
            color: '#1B0826',
            borderColor: '#1B0826',
            '&:hover': {
              borderColor: '#1B0826',
              bgcolor: 'rgba(27, 8, 38, 0.04)',
            },
          }}
        >
          Add Component
        </Button>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: '2px dashed #1976d2',
            borderRadius: 2,
            bgcolor: '#f0f7ff',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" fontWeight={600}>
              New Component
            </Typography>
            <IconButton size="small" onClick={resetAddForm}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Type selection */}
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40 }}>Type:</Typography>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={newType}
                onChange={(e) => {
                  setNewType(e.target.value as any);
                  setNewEntity('');
                  setNewFeature('');
                  setNewMetadataField('');
                  setNewMetadataValue('');
                }}
                displayEmpty
                sx={selectSx('#c4c4f7')}
              >
                <MenuItem value=""><em>select type</em></MenuItem>
                <MenuItem value="entity_frequency">Entity Frequency</MenuItem>
                <MenuItem value="feature">Feature</MenuItem>
                <MenuItem value="metadata">Metadata</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Type-specific fields */}
          {newType === 'entity_frequency' && (
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40 }}>Entity:</Typography>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={newEntity}
                  onChange={(e) => setNewEntity(e.target.value)}
                  displayEmpty
                  sx={selectSx('#b8e6d5')}
                >
                  <MenuItem value=""><em>select entity</em></MenuItem>
                  {entityNames.map((name) => (
                    <MenuItem key={name} value={name}>
                      {entityDefinitions[name]?.name || entityDefinitions[name]?.displayName || name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {newType === 'feature' && (
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40 }}>Feature:</Typography>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  displayEmpty
                  sx={selectSx('#b8e6d5')}
                >
                  <MenuItem value=""><em>select feature</em></MenuItem>
                  {featureNames.map((name) => (
                    <MenuItem key={name} value={name}>
                      {featureDefinitions[name]?.displayName || name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {newType === 'metadata' && (
            <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40 }}>Field:</Typography>
              <TextField
                value={newMetadataField}
                onChange={(e) => setNewMetadataField(e.target.value)}
                size="small"
                placeholder="field name"
                sx={{ width: 140, ...greenInputSx }}
              />
              <Typography variant="caption" color="text.secondary">=</Typography>
              <TextField
                value={newMetadataValue}
                onChange={(e) => setNewMetadataValue(e.target.value)}
                size="small"
                placeholder="required value"
                sx={{ width: 140, ...smallInputSx }}
              />
            </Box>
          )}

          {/* Max points */}
          {newType && (
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40 }}>Points:</Typography>
              <TextField
                type="number"
                value={newMaxPoints}
                onChange={(e) => setNewMaxPoints(Math.max(0, Math.min(50, parseInt(e.target.value) || 0)))}
                size="small"
                sx={{ width: 70, ...smallInputSx }}
                inputProps={{ min: 0, max: 50 }}
              />
            </Box>
          )}

          {/* Actions */}
          <Box display="flex" gap={1}>
            <SecondaryButton onClick={resetAddForm} fullWidth>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={handleSaveNewComponent}
              fullWidth
              disabled={
                !newType ||
                (newType === 'entity_frequency' && !newEntity) ||
                (newType === 'feature' && !newFeature) ||
                (newType === 'metadata' && !newMetadataField)
              }
            >
              Add
            </PrimaryButton>
          </Box>
        </Paper>
      )}

      {components.length === 0 && !addingComponent && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No configurable components yet. Click "Add Component" to get started.
        </Alert>
      )}
    </Box>
  );
};
